/**
 * useEntityDetection Hook
 * Debounced AI-powered entity detection for document content
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useDocumentStore, useDetectionState } from '@/stores/documentStore'
import type { DetectionStatus } from '@/types/document'

interface UseEntityDetectionOptions {
  documentId: string | null
  content: string
  enabled?: boolean
  debounceMs?: number
  minContentLength?: number
}

interface UseEntityDetectionResult {
  status: DetectionStatus
  isAnalyzing: boolean
  detectedCount: number
  error?: string
  triggerAnalysis: () => void
}

export function useEntityDetection({
  documentId,
  content,
  enabled = true,
  debounceMs = 3000,  // 3 seconds after last keystroke
  minContentLength = 100,
}: UseEntityDetectionOptions): UseEntityDetectionResult {
  const analyzeDocument = useDocumentStore((state) => state.analyzeDocument)
  const detectionState = useDetectionState()

  // Get document to check hasUserContent flag
  const document = useDocumentStore((state) =>
    documentId ? state.documents.find((d) => d.id === documentId) : null
  )

  // Skip analysis for template documents that haven't been edited yet
  const shouldAnalyze = enabled && (
    !document?.templateId ||  // Not a template doc
    document?.hasUserContent === true  // Template doc with user edits
  )

  const [manualTrigger, setManualTrigger] = useState(0)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastAnalyzedContentRef = useRef<string>('')

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Debounced analysis
  useEffect(() => {
    if (!shouldAnalyze || !documentId) return
    if (content.length < minContentLength) return

    // Skip if content hasn't changed
    if (content === lastAnalyzedContentRef.current) return

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(async () => {
      lastAnalyzedContentRef.current = content
      await analyzeDocument(documentId)
    }, debounceMs)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [content, documentId, shouldAnalyze, debounceMs, minContentLength, analyzeDocument])

  // Manual trigger effect
  useEffect(() => {
    if (manualTrigger > 0 && documentId && content.length >= minContentLength) {
      lastAnalyzedContentRef.current = content
      analyzeDocument(documentId)
    }
  }, [manualTrigger, documentId, content, minContentLength, analyzeDocument])

  // Manual trigger function
  const triggerAnalysis = useCallback(() => {
    setManualTrigger((prev) => prev + 1)
  }, [])

  return {
    status: detectionState.status,
    isAnalyzing: detectionState.status === 'analyzing',
    detectedCount: detectionState.detectedCount || 0,
    error: detectionState.error,
    triggerAnalysis,
  }
}
